import { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Printer, MessageCircle, Loader2 } from 'lucide-react';
import { formatMoeda, formatQtd } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { useToast } from '../Toast';
import { ModalEnviarWhatsAppCliente } from '../whatsapp/ModalEnviarWhatsAppCliente';
import { htmlParaJpg } from '../../utils/html-para-jpg';
import { gerarExtratoConsignacaoHTML, paginarPedidos } from '@fiodecobre/shared';
import { getToken } from '../../lib/api';

// Extrato de consignacao (DA-383): PREVIEW so-leitura do orcamento UNIFICADO — junta os itens de
// todos os venda_cm em aberto do cliente num documento so, o mesmo que a nota do "Fechar o mes" vai
// conter. Nao emite nada, nao grava nada; e' pra o operador conferir antes de fechar. Itens iguais
// (mesmo codigo + mesmo preco travado) somam; precos diferentes ficam em linhas separadas.
//
// SAIDA PRO CLIENTE (DA-417): Imprimir + Enviar WhatsApp. O documento que SAI daqui NAO e' esta
// tela — a tela unifica (e' a previa da nota, pro operador); o extrato do cliente sai SEPARADO POR
// PEDIDO, com a data da retirada e quem retirou, porque quem le do outro lado precisa bater com as
// idas ao balcao. Duas leituras do mesmo saldo, cada uma pra uma pergunta.

interface ItemResumo {
  codigo: string | null;
  descricao: string | null;
  quantidade: string;
  preco_unitario: string;
  saldo_valor: string;
}
interface PedidoResumo {
  pedido_id: number;
  numero: string;
  liberado_em: string;
  comprador: string | null;
  total_saldo_valor: string;
  itens: ItemResumo[];
}
interface Props {
  clienteId: number;
  clienteNome: string;
  pedidos: PedidoResumo[];
  total: number;
  onClose: () => void;
}

export function ModalExtratoConsignacao({ clienteId, clienteNome, pedidos, total, onClose }: Props) {
  const toast = useToast();
  const [enviando, setEnviando] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [modalEnvio, setModalEnvio] = useState(false);
  // Os JPGs sao caros de gerar (html2canvas por pagina) e o modal de envio dispara `enviarPara`
  // UMA VEZ POR DESTINATARIO — sem cache, mandar pra 3 contatos renderizaria tudo 3 vezes.
  const jpgsRef = useRef<string[] | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !enviando) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, enviando]);

  // Monta o documento do CLIENTE (por pedido), nao a vista unificada da tela.
  function paginasHtml(): string[] {
    const paginas = paginarPedidos(pedidos);
    return paginas.map((pg, i) => gerarExtratoConsignacaoHTML({
      clienteNome,
      pedidos: pg,
      total,
      pagina: { atual: i + 1, total: paginas.length, mostrarTotal: i === paginas.length - 1 },
    }));
  }

  // PDF pelo SERVIDOR (Puppeteer), nao pelo `window.print()`: o dialogo do Chrome travava em
  // "Salvando..." pra sempre com o preview desenhado certo. Aqui o servidor devolve o arquivo
  // pronto e o navegador so baixa — nenhum dialogo no meio. Mesmo caminho da Autorizacao.
  async function baixarPdf() {
    if (baixando) return;
    setBaixando(true);
    try {
      const r = await fetch(`/api/consignacao/${clienteId}/extrato-pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.erro || 'Erro ao gerar o PDF.');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Extrato_${clienteNome.replace(/[^\w]+/g, '_')}.pdf`;
      // Anexar ao DOM ANTES do click (o Firefox exige) e revogar TARDE — revogar na mesma pilha
      // cancela o download antes de o navegador comecar. Ver CLAUDE.md, "Download de blob".
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e: any) {
      toast.erro(e?.message || 'Erro ao gerar o PDF.');
    } finally {
      setBaixando(false);
    }
  }

  async function enviarPara(digits: string) {
    if (!jpgsRef.current) {
      jpgsRef.current = [];
      for (const html of paginasHtml()) jpgsRef.current.push(await htmlParaJpg(html));
    }
    const jpgs = jpgsRef.current;
    const base = `Extrato_${clienteNome.replace(/[^\w]+/g, '_')}`;
    for (let i = 0; i < jpgs.length; i++) {
      // Endpoint do CR reusado de proposito: por dentro ele so recebe jid + imagem e resolve a
      // janela de 24h / desvio pro numero de Compras (DA-336). Clonar isso pra ca criaria uma 2a
      // copia daquela regra delicada, que envelheceria sozinha. So o caminho da rota tem o nome
      // "contas-receber" — nada dentro dele toca em titulo.
      await api.post('/api/contas-receber/enviar-whatsapp-extrato', {
        jid: digits,
        imagem_base64: jpgs[i],
        filename: jpgs.length > 1 ? `${base}_p${i + 1}.jpg` : `${base}.jpg`,
        caption: i === 0
          ? `Extrato de material em aberto — ${clienteNome}. Confira e nos avise se algo nao conferir.`
          : null,
      });
    }
  }

  const itens = useMemo(() => {
    const map = new Map<string, { codigo: string | null; descricao: string | null; quantidade: number; preco_unitario: number; saldo_valor: number }>();
    for (const p of pedidos) {
      for (const it of (p.itens ?? [])) {
        const key = `${it.codigo ?? ''}|${it.preco_unitario}`;
        const q = Number(it.quantidade); const sv = Number(it.saldo_valor); const pu = Number(it.preco_unitario);
        const cur = map.get(key);
        if (cur) { cur.quantidade += q; cur.saldo_valor += sv; }
        else map.set(key, { codigo: it.codigo, descricao: it.descricao, quantidade: q, preco_unitario: pu, saldo_valor: sv });
      }
    }
    return [...map.values()].sort((a, b) => (a.descricao ?? '').localeCompare(b.descricao ?? ''));
  }, [pedidos]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-t-xl text-white" style={{ backgroundColor: '#12294f', borderBottom: '2px solid #1d4ed8' }}>
          <FileText size={18} />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-base">Extrato — orcamento unificado</div>
            <div className="text-xs truncate" style={{ color: '#a9bbe0' }}>{clienteNome}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar"
            className="w-8 h-8 rounded-lg text-blue-100 hover:bg-white/10">✕</button>
        </div>

        <div className="p-5 overflow-y-auto">
          <div className="flex gap-2.5 items-start rounded-lg px-3 py-2.5 mb-4 text-sm" style={{ backgroundColor: '#f3f6fc', border: '1px solid #e2e7f0', color: '#47526b' }}>
            <FileText size={16} style={{ color: '#1d4ed8', flexShrink: 0, marginTop: 1 }} />
            <span>Prévia do que vai na <b>nota do fechamento</b> — todos os {pedidos.length} pedidos em aberto juntos, no preço travado. Só exibição: <b>não</b> emite nem grava nada.</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#8791a6' }} className="text-[11px] uppercase tracking-wide">
                  <th className="text-left font-bold px-2.5 py-2" style={{ borderBottom: '1.5px solid #e2e7f0', width: 60 }}>Cód.</th>
                  <th className="text-left font-bold px-2.5 py-2" style={{ borderBottom: '1.5px solid #e2e7f0' }}>Produto</th>
                  <th className="text-right font-bold px-2.5 py-2" style={{ borderBottom: '1.5px solid #e2e7f0', width: 80 }}>Qtd</th>
                  <th className="text-right font-bold px-2.5 py-2" style={{ borderBottom: '1.5px solid #e2e7f0', width: 90 }}>Unit.</th>
                  <th className="text-right font-bold px-2.5 py-2" style={{ borderBottom: '1.5px solid #e2e7f0', width: 100 }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((it, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eef1f6' }}>
                    <td className="px-2.5 py-2 tabular-nums" style={{ fontFamily: 'monospace', color: '#47526b' }}>{it.codigo ?? '—'}</td>
                    <td className="px-2.5 py-2 font-semibold" style={{ color: '#0f1b33' }}>{it.descricao ?? '—'}</td>
                    <td className="px-2.5 py-2 text-right tabular-nums" style={{ color: '#47526b' }}>{formatQtd(it.quantidade)}</td>
                    <td className="px-2.5 py-2 text-right tabular-nums" style={{ color: '#47526b' }}>{formatMoeda(it.preco_unitario)}</td>
                    <td className="px-2.5 py-2 text-right tabular-nums font-bold" style={{ color: '#0f1b33' }}>{formatMoeda(it.saldo_valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center gap-3 px-5 py-3.5 rounded-b-xl mt-auto" style={{ borderTop: '1px solid #e2e7f0', backgroundColor: '#fafbfe' }}>
          <div className="text-sm" style={{ color: '#47526b' }}>
            {itens.length} {itens.length === 1 ? 'item' : 'itens'} · <b style={{ color: '#0f1b33' }}>total</b>{' '}
            <b style={{ color: '#1d4ed8' }}>{formatMoeda(total)}</b>
          </div>
          <div className="flex-1" />
          <button type="button" onClick={baixarPdf} disabled={enviando || baixando}
            className="px-4 py-2 rounded-lg font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: '#fff', color: '#1d4ed8', border: '1.5px solid #1d4ed8' }}>
            {baixando ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />}
            {baixando ? 'Gerando...' : 'Salvar PDF'}
          </button>
          <button type="button" onClick={() => setModalEnvio(true)} disabled={enviando}
            className="px-4 py-2 rounded-lg font-bold text-sm text-white inline-flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: '#16a34a' }}>
            {enviando ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}
            Enviar WhatsApp
          </button>
          <button type="button" onClick={onClose} disabled={enviando}
            className="px-5 py-2 rounded-lg font-bold text-sm text-white disabled:opacity-50"
            style={{ backgroundColor: '#1d4ed8' }}>Fechar</button>
        </div>
      </div>

      {modalEnvio && (
        <ModalEnviarWhatsAppCliente
          clienteId={clienteId}
          titulo="Enviar extrato pelo WhatsApp"
          contextoNome={clienteNome}
          contextoExtra={`${pedidos.length} ${pedidos.length === 1 ? 'pedido' : 'pedidos'} · ${formatMoeda(total)}`}
          arquivoLabel="Extrato de material em aberto"
          enviarPara={async (digits) => {
            setEnviando(true);
            try { await enviarPara(digits); }
            finally { setEnviando(false); }
          }}
          onClose={(enviouAlgum) => {
            setModalEnvio(false);
            if (enviouAlgum) toast.sucesso('Extrato enviado.');
          }}
        />
      )}
    </div>
  );
}
